// src/contexts/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  User as FirebaseUser,
  signOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import {
  auth,
  db,
  functions,
  authDiagnostic,
  getAuthenticatedFirebaseUser,
  setCanonicalFirebaseUser,
} from '../services/firebase';
import { User } from '../types';
import { isSystemManager } from '../utils/authorization';

const teacherRoleRepairAttempts = new Set<string>();

const requestTeacherRoleRepair = async (
  firebaseUser: FirebaseUser,
  userData: User
): Promise<boolean> => {
  if (userData.role !== 'student') return false;
  if (teacherRoleRepairAttempts.has(firebaseUser.uid)) return false;

  teacherRoleRepairAttempts.add(firebaseUser.uid);

  try {
    const repairTeacherUserRole = httpsCallable<
      Record<string, never>,
      { repaired: boolean; reason: string }
    >(functions, 'auth-repairTeacherUserRole');
    const result = await repairTeacherUserRole({});

    if (result.data.repaired) {
      console.info('AuthContext: Teacher role repaired; waiting for refreshed user document.', {
        uid: firebaseUser.uid,
      });
      return true;
    }
  } catch (error) {
    console.warn('AuthContext: Teacher role repair check failed:', error);
  }

  return false;
};

const isConfiguredSystemManager = async (user: User): Promise<boolean> => {
  if (user.role !== 'admin') return true;
  if (!isSystemManager(user)) return false;

  try {
    const securitySnapshot = await getDoc(
      doc(db, 'systemSettings', 'security')
    );
    return (
      securitySnapshot.exists() &&
      securitySnapshot.data().systemManagerUid === user.id
    );
  } catch (error) {
    console.error('AuthContext: Failed to verify system manager:', error);
    return false;
  }
};

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Refresh user data from Firestore
  const refreshUser = async () => {
    if (firebaseUser) {
      // This will be handled by the Firestore listener
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Listen to Firebase Auth state changes
    authDiagnostic('AUTH_STATE_SUBSCRIPTION_REGISTERED', {
      effectFirebaseUserUid: firebaseUser?.uid || null,
    });
    const unsubscribeAuth = onAuthStateChanged(auth, firebaseUser => {
      authDiagnostic('AUTH_STATE_CALLBACK', {
        callbackUserUid: firebaseUser?.uid || null,
        callbackCurrentUserUid: auth.currentUser?.uid || null,
      });
      setCanonicalFirebaseUser(firebaseUser);
      setFirebaseUser(firebaseUser);

      if (firebaseUser) {
        // Listen to user document changes in Firestore
        const unsubscribeUser = onSnapshot(
          doc(db, 'users', firebaseUser.uid),
          async documentSnapshot => {
            if (documentSnapshot.exists()) {
              const userData = {
                ...documentSnapshot.data(),
                id: firebaseUser.uid, // إضافة معرف المستخدم
                createdAt: documentSnapshot.data().createdAt?.toDate(),
                updatedAt: documentSnapshot.data().updatedAt?.toDate(),
              } as User;

              // إذا لم يكن الاسم موجود في Firestore، استخدم displayName من Firebase Auth
              if (!userData.name && firebaseUser.displayName) {
                userData.name = firebaseUser.displayName;
              }

              // إذا كان displayName موجود وأفضل من الاسم في Firestore، استخدمه
              if (
                firebaseUser.displayName &&
                firebaseUser.displayName !== 'مستخدم جديد'
              ) {
                userData.name = firebaseUser.displayName;
              }

              console.log('AuthContext: User data loaded:', {
                name: userData.name,
                displayName: firebaseUser.displayName,
                role: userData.role,
                email: userData.email,
              });
              authDiagnostic('AUTH_CONTEXT_PROFILE_LOADED', {
                profileUid: firebaseUser.uid,
                profileRole: userData.role,
                profileOrganizationId:
                  (userData as User & { organizationId?: string }).organizationId ||
                  userData.tenantId ||
                  null,
              });

              if (await requestTeacherRoleRepair(firebaseUser, userData)) {
                setLoading(true);
                return;
              }

              if (!(await isConfiguredSystemManager(userData))) {
                console.warn(
                  'AuthContext: Refusing an unconfigured admin session.',
                  { uid: firebaseUser.uid }
                );
                setUser(null);
                setLoading(false);
                await signOut(auth);
                return;
              }

              const canonicalAuthUser = await getAuthenticatedFirebaseUser().catch(() => null);
              if (!canonicalAuthUser || canonicalAuthUser.uid !== firebaseUser.uid) {
                setUser(null);
                setLoading(true);
                return;
              }
              await canonicalAuthUser.getIdToken();

              authDiagnostic('AUTH_CONTEXT_USER_PUBLISHED', {
                userUid: userData.id,
                userRole: userData.role,
                userOrganizationId:
                  (userData as User & { organizationId?: string }).organizationId ||
                  userData.tenantId ||
                  null,
              });
              setUser(userData);
            } else {
              console.log(
                'AuthContext: User document does not exist, retrying in 2 seconds...'
              );
              // إعادة محاولة واحدة بعد 2 ثانية للمستخدمين الجدد
              setTimeout(async () => {
                try {
                  const retryDoc = await getDoc(
                    doc(db, 'users', firebaseUser.uid)
                  );
                  if (retryDoc.exists()) {
                    const docData = retryDoc.data() as any;
                    const userData = {
                      ...docData,
                      id: firebaseUser.uid, // إضافة معرف المستخدم
                      createdAt: docData.createdAt?.toDate(),
                      updatedAt: docData.updatedAt?.toDate(),
                    } as User;
                    console.log('AuthContext: User data loaded on retry:', {
                      name: userData.name,
                      role: userData.role,
                      email: userData.email,
                    });

                    if (await requestTeacherRoleRepair(firebaseUser, userData)) {
                      setLoading(true);
                      return;
                    }

                    if (!(await isConfiguredSystemManager(userData))) {
                      console.warn(
                        'AuthContext: Refusing an unconfigured admin session on retry.',
                        { uid: firebaseUser.uid }
                      );
                      setUser(null);
                      setLoading(false);
                      await signOut(auth);
                      return;
                    }

                    const canonicalAuthUser = await getAuthenticatedFirebaseUser().catch(() => null);
                    if (!canonicalAuthUser || canonicalAuthUser.uid !== firebaseUser.uid) {
                      setUser(null);
                      setLoading(true);
                      return;
                    }
                    await canonicalAuthUser.getIdToken();

                    setUser(userData);
                    setLoading(false);
                    return;
                  }
                } catch (retryError) {
                  console.error('AuthContext: Retry failed:', retryError);
                }

                console.warn(
                  'AuthContext: User document still missing after retry; refusing fallback role inference.',
                  { uid: firebaseUser.uid, email: firebaseUser.email }
                );
                setUser(null);
                setLoading(false);
              }, 2000);
            }
            setLoading(false);
          },
          error => {
            authDiagnostic('AUTH_CONTEXT_PROFILE_ERROR', {
              errorCategory: error?.code || error?.name || 'unknown',
            });
            console.error('Error listening to user document:', error);
            setUser(null);
            setLoading(false);
          }
        );

        // Return cleanup function for user document listener
        return () => unsubscribeUser();
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    // Cleanup auth listener
    return () => unsubscribeAuth();
  }, [firebaseUser?.uid]);

  const value: AuthContextType = {
    user,
    firebaseUser,
    loading,
    isAuthenticated: !!user,
    refreshUser,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
